import { z } from 'zod';
import { publicCountryConfigs } from '../country/public-registry.js';
import type { PublicCountryConfig } from '../country/types.js';

const hexKey64 = /^[a-f0-9]{64}$/i;

/**
 * A boolean env flag that can actually be turned OFF.
 *
 * NEVER use `z.coerce.boolean()` here. It is `Boolean(value)`, and env values are
 * always strings, so `"false"`, `"0"`, `"no"` and `"off"` all coerce to **true** —
 * only unset or empty yields false. Writing `FLAG=false` to make a default
 * explicit therefore turns the feature ON, which is the opposite of what every
 * reader of that line expects.
 *
 * Accepts `true/1/yes/on/y` and `false/0/no/off/n`, case- and whitespace-
 * insensitive. Absent (undefined, null, empty) falls back to `def`. Anything
 * else is a TYPO and throws at boot rather than being guessed at.
 *
 * The first version of this helper only accepted the exact literals `'true'` and
 * `'1'` and treated everything else as `false`. That fixed the default-false
 * flags but introduced a worse bug on the one default-TRUE flag
 * (OCTO_FISCAL_ENABLED): `TRUE`, `yes`, `on` and `1` all read as **false**,
 * silently disabling fiscalization — cases the old `coerce` got right. It also
 * inverted a real boolean `true` to `false` when handed a JS config object, and
 * let `null` override a `true` default. Guessing at an unrecognised value is how
 * this whole class of bug started, so an unparseable flag now fails loudly.
 */
const BOOL_TRUE = new Set(['true', '1', 'yes', 'on', 'y']);
const BOOL_FALSE = new Set(['false', '0', 'no', 'off', 'n']);

const envBool = (def: boolean) =>
  z.preprocess((v) => {
    // Absent → the declared default. `null` counts: compose and some config
    // loaders materialise one, and letting it fall through to `false` would
    // quietly override a default of `true`.
    if (v === undefined || v === null || v === '') return def;
    // A real boolean or 0/1 arrives when the schema is handed a JS config object
    // rather than process.env. Stringifying those inverted `true` → `false`.
    if (typeof v === 'boolean') return v;
    if (v === 0) return false;
    if (v === 1) return true;

    // Only a string is normalisable. Anything else (an object, a number other
    // than 0/1) falls through unchanged and `z.boolean()` rejects it — which is
    // the intent, and avoids stringifying an object to "[object Object]".
    if (typeof v !== 'string') return v;
    const s = v.trim().toLowerCase();
    if (BOOL_TRUE.has(s)) return true;
    if (BOOL_FALSE.has(s)) return false;

    // Unrecognised: return it unchanged so `z.boolean()` rejects and `parseEnv`
    // throws at boot naming the key — the same treatment a malformed
    // ENCRYPTION_KEY already gets.
    return v;
  }, z.boolean());

/**
 * Per-account read ceilings for the three things a Pro subscription actually
 * buys — buyer contacts, tender documents, the CSV export.
 *
 * Named here rather than written inline as `.default(20)` so the numbers exist
 * exactly once: the env schema, `ReadLimitsService` and
 * `docs/explanation/tender-detail-entitlements.md` all point at this object, and
 * a change cannot land in one and miss the others.
 *
 * WHERE THEY COME FROM (measured on prod 2026-09-23, 7-day window): 90 Pro
 * accounts read a buyer contact. Median 13.5 views, p95 149, max 216 — and that
 * heaviest account touched 32 distinct lots, at most 137 in one day and at most
 * 5 in one minute. That is a human broker re-opening lots, and every one of
 * those numbers sits comfortably under these ceilings. 20/minute and 200/day is
 * the owner's call for "already strange for a human" (Igor, 2026-09-23), not a
 * percentile of the distribution above.
 */
export const READ_LIMIT_DEFAULTS = {
  contactsPerMinute: 20,
  contactsPerDay: 200,
  documentsPerMinute: 20,
  documentsPerDay: 200,
  exportsPerDay: 10,
} as const;

const baseSchema = z.object({
  NODE_ENV: z.enum(['development', 'test', 'production']).default('development'),
  PORT: z.coerce.number().int().default(47611),

  // Database
  DATABASE_URL: z.string().url(),

  // Auth
  JWT_SECRET: z.string().min(32),
  JWT_EXPIRY: z.string().default('30d'),

  // Encryption — AES-256-GCM key for CryptoService (32 bytes / 64 hex chars)
  ENCRYPTION_KEY: z
    .string()
    .regex(hexKey64, 'ENCRYPTION_KEY must be a 64-character hex string (32 bytes)'),

  // Redis
  REDIS_URL: z.string().url().default('redis://localhost:47663'),

  // Server
  FRONTEND_URL: z.string().url().default('http://localhost:47620'),

  // Logging — pino level for the api logger
  LOG_LEVEL: z.enum(['fatal', 'error', 'warn', 'info', 'debug', 'trace', 'silent']).default('info'),

  // Tender ingestion
  TENDER_INGEST_ENABLED: envBool(false),
  TENDER_SOURCE_DEFAULT_KEY: z.string().optional(),
  TENDER_INGEST_CONCURRENCY: z.coerce.number().int().default(1),
  TENDER_INGEST_CRON: z.string().default('0 */6 * * *'),
  TENDER_SOURCE_FAIL_THRESHOLD: z.coerce.number().int().default(3),

  // Document text extraction — extra formats, both OFF by default (2026-09-24).
  // The parsers run IN-PROCESS in the ingestion worker on a 4-vCPU / 9.7 GB host
  // with no swap, so every format past PDF/DOCX is opt-in until someone has
  // measured what it costs there. OFF means byte-for-byte today's behaviour.
  //
  // `DOC_TEXT_PARSE_OFFICE` — legacy `.doc` (word-extractor, pure JS) plus a
  // richer spreadsheet rendering (whole rows, tab-separated, capped) in place of
  // the unique-string-cell extraction `.xlsx`/`.xls` already get. ⚠️ It does NOT
  // switch spreadsheets ON: they have been parsed since 2026-07-30 and stay
  // parsed when this is false — only the RENDERING changes, and only `.doc` is
  // genuinely new.
  //
  // `DOC_TEXT_PARSE_ARCHIVES` — `.zip` (fflate) and `.rar` (node-unrar-js, WASM)
  // unpacked in memory, one entry at a time, and each inner file run through the
  // same parsers. Hard caps live next to the code (`archive-text.ts`).
  DOC_TEXT_PARSE_OFFICE: envBool(false),
  DOC_TEXT_PARSE_ARCHIVES: envBool(false),

  // AI summarisation + translation
  AI_PROVIDER: z.enum(['stub', 'anthropic']).default('stub'),
  AI_API_KEY: z.string().optional(),
  AI_MODEL: z.string().optional(),
  // Per-language char cap (uz/ru sliced independently in parseBilingual) —
  // sized as a truncation safety net under the 200-token MAX_TOKENS budget
  // for the bilingual completion, not as the primary length control.
  AI_SUMMARY_MAXLEN: z.coerce.number().int().default(1800),

  // «Пакет заявки» (bid package) carries NO env flag. Who may use it is a
  // per-user grant (`feature_grants`, key `bid_package`) — see
  // `docs/how-to/feature-grants.md`. A deploy-wide boolean could not express
  // the thing the cohort is actually bounded by: every generated package is
  // read by a human before it is sent to a real procurement, so the gate has
  // to name PEOPLE. `FEATURES_OPEN_TO_ALL` below is the one dial that opens
  // it to everybody, and it is the same dial every other beta uses.

  // ── ИИ-онбординг (POST /api/ai-search/propose) ────────────────────────────
  //
  // EVERY key here has a default, so prod boots on the existing .env with no
  // change. The model is deliberately NOT `AI_MODEL`: that one is the ingestion
  // workhorse (haiku, thousands of calls a day) and this one is a per-user,
  // once-or-twice-a-lifetime call the owner chose Sonnet for. Sharing the knob
  // would mean a cost decision about summaries silently re-decided the quality
  // of the onboarding proposal, or the reverse.
  //
  // `claude-sonnet-5-5`, the current Sonnet (the bot assistant runs on it too).
  // Its request shape differs from Sonnet 5's — no forced `tool_choice`, no
  // `thinking: disabled` — see `proposeSearchDefinition`. Measured live
  // 2026-10-15 on a Kazakh ask: 3.7 s, 1,263 tokens in / 453 out.
  AI_ONBOARDING_MODEL: z.string().default('claude-sonnet-5-5'),
  // Hard wall-clock budget for the whole model leg, retry included. 12s because
  // Sonnet on this prompt measured 4–10s; past that the client has already shown
  // its fallback and a late answer is worse than none.
  AI_ONBOARDING_TIMEOUT_MS: z.coerce.number().int().default(12_000),
  // USD per MILLION tokens, used to price each call from the token counts the
  // API returns. Defaults are Claude Sonnet 5.5's list price ($2 in / $10 out,
  // the same as Sonnet 5's — the branch once shipped Sonnet 4.6's $3/$15 here,
  // which would have overstated `cost_usd` by 50%). They are env-settable because the invoice is the
  // source of truth and it moves — a hardcoded price is how a spend line goes
  // quietly wrong. Change them together with `AI_ONBOARDING_MODEL`.
  AI_ONBOARDING_PRICE_IN_USD_PER_MTOK: z.coerce.number().default(2),
  AI_ONBOARDING_PRICE_OUT_USD_PER_MTOK: z.coerce.number().default(10),
  // Free: N proposals per account FOREVER (not per month). Paid: N per calendar
  // month (UTC). Only a proposal that came back with at least one code or
  // category is charged — see AiSearchQuotaService.
  AI_ONBOARDING_FREE_LIFETIME: z.coerce.number().int().default(2),
  AI_ONBOARDING_PAID_PER_MONTH: z.coerce.number().int().default(15),

  // Which country deployment this process/build serves. Selects the CountryConfig.
  // Defaults to 'uz'; every existing .env keeps working unmodified.
  COUNTRY: z.enum(['uz', 'kz', 'kg']).default('uz'),

  // Speech-to-text for inbound Telegram voice messages. `groq` uses the
  // OpenAI-compatible Whisper endpoint (GROQ_API_KEY required); `stub` returns
  // a canned transcript for local/dev/test. GROQ_TRANSCRIBE_MODEL defaults to
  // whisper-large-v3-turbo (fast, multilingual — auto-detects uz/ru).
  TRANSCRIPTION_PROVIDER: z.enum(['stub', 'groq']).default('stub'),
  GROQ_API_KEY: z.string().optional(),
  GROQ_TRANSCRIBE_MODEL: z.string().default('whisper-large-v3-turbo'),

  // ── Tax Committee company registry (my.soliq.uz) ──────────────────────────
  // Free "remote-access" API, self-issued key. Resolves a 9-digit СТИР to the
  // full registry record (36 fields: name, ОКЭД, status, tax mode, address).
  //
  // ⚠️ UZ-IP-ONLY. It does not answer from anywhere else, so it CANNOT be
  // exercised locally — absent key ⇒ the client is disabled and callers fall
  // back, which is also what every dev machine and CI runner sees.
  SOLIQ_API_KEY: z.string().optional(),
  SOLIQ_BASE_URL: z.string().url().default('https://my.soliq.uz/api/remote-access-api'),

  // ── goszakup.gov.kz official API (Kazakhstan) ─────────────────────────────
  // Bearer token for «Унифицированные сервисы v3» (`ows.goszakup.gov.kz/v3/graphql`), issued
  // to the owner on 2026-10-13. OPTIONAL, and the whole switch:
  //   set   → `GoszakupAdapter` harvests every open lot through the API (deadline, customer
  //           БИН, КАТО, ЕНС ТРУ, official Kazakh names — one request per 200 lots), and the
  //           KZ contracts step reads the API's contract registry;
  //   unset → both stay on the public HTML site (`old.goszakup.gov.kz`), exactly as before.
  // Uzbekistan never sets it and is unaffected. Read straight from `process.env` by the
  // adapter (the standalone scheduler wires adapters without Nest DI — same idiom as
  // `BRIGHT_DATA_API_KEY`); listed here so the name is documented in one place.
  // (An earlier GOSZAKUP_TOKEN entry was removed 2026-09-14, when no key had been obtained.)
  GOSZAKUP_TOKEN: z.string().optional(),

  // Embedding (semantic search). `openai` uses text-embedding-3-* via EMBED_API_KEY;
  // set EMBED_DIM to match the model (text-embedding-3-small = 1536).
  EMBED_PROVIDER: z.enum(['stub', 'openai']).default('stub'),
  EMBED_DIM: z.coerce.number().int().default(16),
  EMBED_API_KEY: z.string().optional(),
  EMBED_MODEL: z.string().optional(),

  // Payment. PAYMENT_PROVIDER is the DEFAULT/fallback gateway. PAYMENT_METHODS is
  // an optional comma-separated list of the user-selectable methods at checkout
  // (e.g. "payme,click") — when set, the web shows a button per method and checkout
  // routes to the chosen one. When empty it falls back to [PAYMENT_PROVIDER].
  // `click` was once removed from this enum after its webhook route was deleted (a
  // free-Pro hole). It is BACK: Click is genuinely onboarded, its confirmation
  // path is the dedicated POST /billing/click endpoint (ClickWebhookService), and
  // that handler fails closed — it refuses a callback when CLICK_SECRET_KEY or
  // CLICK_SERVICE_ID is unset, and never confirms a non-Click order. So a
  // half-configured Click cannot silently grant Pro. Usually Click is offered
  // ALONGSIDE Payme via PAYMENT_METHODS, with PAYMENT_PROVIDER left at payme.
  // `stripe` (2026-10-13) is the Kazakhstan gateway: Stripe Checkout in
  // subscription mode, confirmed by the signed webhook at POST /billing/stripe.
  // Like Click it fails closed — no STRIPE_SECRET_KEY means no checkout can be
  // created, no STRIPE_WEBHOOK_SECRET means every callback is refused.
  PAYMENT_PROVIDER: z.enum(['stub', 'click', 'payme', 'octo', 'stripe']).default('stub'),
  PAYMENT_METHODS: z.string().optional(),
  // Stripe (Kazakhstan). All optional: the provider is dormant unless set.
  //  - STRIPE_SECRET_KEY: the RESTRICTED live key (`rk_live_…`). Needs write on
  //    Checkout Sessions, Customers and Subscriptions, read on Prices. Never
  //    logged, never returned by any endpoint.
  //  - STRIPE_WEBHOOK_SECRET: the signing secret (`whsec_…`) of the webhook
  //    endpoint that points at `${API_PUBLIC_URL}/billing/stripe`. Without it the
  //    webhook refuses every request, so nothing can be granted.
  // How to obtain both, and the go-live order: docs/how-to/stripe-kz-go-live.md.
  STRIPE_SECRET_KEY: z.string().optional(),
  STRIPE_WEBHOOK_SECRET: z.string().optional(),
  // Staged OCTO rollout: comma-separated emails allowed to see/use the card
  // (OCTO) checkout option while it's in test. Empty = OCTO offered to everyone
  // (subject to PAYMENT_METHODS).
  OCTO_ALLOWLIST_EMAILS: z.string().optional(),
  // Comma-separated emails that additionally see the cheap `test-5k` plan on
  // /plans and may buy it. For exercising the real money path — card → Payme →
  // Merchant-API callback → activation — at 5 000 so'm instead of 399 000.
  // The ONLY way in, again: a whole-domain rule (`test.uz`) was entitled in
  // code between 2026-09-21 and 2026-09-22 and the owner removed it, because
  // anyone can register an address at that domain. Empty/unset (the default,
  // including prod until someone sets it) = nobody sees it, so this can never
  // widen what a normal visitor is offered.
  TEST_PLAN_ALLOWLIST_EMAILS: z.string().optional(),
  PAYMENT_API_KEY: z.string().optional(),
  PAYMENT_WEBHOOK_SECRET: z.string().optional(),
  USD_UZS_RATE: z.coerce.number().default(12600),

  // Click (Узбекистан) SHOP-API gateway — required when Click is offered (as
  // PAYMENT_PROVIDER=click, or listed in PAYMENT_METHODS). SERVICE_ID + MERCHANT_ID
  // build the my.click.uz checkout link; SECRET_KEY signs the inbound Prepare/
  // Complete callbacks (both verified against CLICK_SERVICE_ID + the MD5 secret,
  // fail-closed — see ClickWebhookService). MERCHANT_USER_ID is only for the
  // optional Invoice API (push-to-phone), unused by the checkout flow. Left
  // optional (like PAYME_*/OCTO_*): the runtime refuses to confirm money when a
  // required var is missing, so an unset key degrades to "no Pro granted", never
  // "Pro granted for free".
  // Staged Click rollout: comma-separated emails allowed to see/use the Click
  // checkout option while it's in test. Empty/unset = Click offered to everyone
  // (subject to PAYMENT_METHODS listing `click`). Mirrors OCTO_ALLOWLIST_EMAILS.
  CLICK_ALLOWLIST_EMAILS: z.string().optional(),
  CLICK_SERVICE_ID: z.string().optional(),
  CLICK_MERCHANT_ID: z.string().optional(),
  CLICK_MERCHANT_USER_ID: z.string().optional(),
  CLICK_SECRET_KEY: z.string().optional(),
  CLICK_PAYMENT_URL: z.string().url().default('https://my.click.uz/services/pay'),

  // Payme (Paycom) Merchant API — required only when PAYMENT_PROVIDER=payme.
  // PAYME_KEY is the касса's Merchant API password (used for Basic auth,
  // login "Paycom"). PAYME_ACCOUNT_FIELD is the account parameter name configured
  // in the касса (Payme POSTs params.account[<field>]); we resolve it to a
  // PaymentOrder.orderNumber.
  PAYME_MERCHANT_ID: z.string().optional(),
  PAYME_KEY: z.string().optional(),
  PAYME_ACCOUNT_FIELD: z.string().default('order_id'),
  PAYME_CHECKOUT_URL: z.string().url().default('https://checkout.paycom.uz'),
  // Payme Subscribe / Cards API — a SEPARATE cassa from the one-time Merchant API
  // above (card tokenization + recurring charges). Card methods auth with the bare
  // cassa id; receipt (charge) methods with `cassaId:key`. Recurring/auto-renewal
  // is off unless both are set.
  PAYME_SUBSCRIBE_CASSA_ID: z.string().optional(),
  PAYME_SUBSCRIBE_KEY: z.string().optional(),
  PAYME_SUBSCRIBE_CHECKOUT_URL: z.string().url().default('https://checkout.paycom.uz'),
  // Staged rollout of card-on-file recurring: comma-separated emails allowed to
  // see/use the auto-renewal card UI + endpoints. Empty = enabled for everyone.
  PAYME_RECURRING_ALLOWLIST_EMAILS: z.string().optional(),
  // Comma-separated emails that may see test plans (`test-*`) as normal tariffs.
  // Empty/unset = hidden from everyone (opposite of the recurring allowlist).
  // Same predicate as TEST_PLAN_ALLOWLIST_EMAILS above — the two vars are
  // separate and have drifted apart before, which is why the matching itself is
  // shared code (`billing/test-plan-email.ts`) even though the lists are not.
  PAYME_TEST_PLAN_ALLOWLIST_EMAILS: z.string().optional(),

  // OCTO (Octobank) gateway — required only when PAYMENT_PROVIDER=octo.
  // Server-to-server: POST {OCTO_BASE_URL}/prepare_payment with octo_shop_id +
  // octo_secret → returns octo_pay_url. Notifications (POST /billing/octo) are
  // verified with OCTO_NOTIFY_SECRET (the Octo-issued "unique_key") via
  // sha1(unique_key + octo_payment_UUID + status). OCTO_TEST toggles the sandbox
  // (a `test:true` flag in the request body — no separate host). Fiscalization
  // (/api/v1/fiscalization/*) reuses OCTO_SHOP_ID + OCTO_SECRET.
  OCTO_SHOP_ID: z.string().optional(),
  OCTO_SECRET: z.string().optional(),
  OCTO_NOTIFY_SECRET: z.string().optional(),
  OCTO_BASE_URL: z.string().url().default('https://secure.octo.uz'),
  OCTO_TEST: envBool(false),
  // Auto-renewal charger (scheduler): when true, saved OCTO card tokens are
  // charged just before period end. Off by default so it stays dormant until
  // OCTO is validated in prod (PAYMENT_PROVIDER can remain payme meanwhile).
  OCTO_AUTORENEW_ENABLED: envBool(false),
  // OCTO fiscalization — the cheque is created by the explicit fiscalization API
  // (`POST /api/v1/fiscalization/receipt`, help.octo.uz), called AFTER a payment
  // succeeds from OctoWebhookService (subscribe) and OctoCharger (renewal). Unlike
  // the older prepare_payment `basket[]` (which needs a cabinet-registered product),
  // this endpoint takes the ИКПУ codes inline, so no cabinet Product Template setup
  // is required. Codes on tasnif.soliq.uz: SPIC 10305001001000000 + packageCode
  //   1504169 («услуга (раз)») — deliberately the SAME ИКПУ the Payme fiscal cassa
  //   uses (chosen with the accountant 2026-07-13) so both gateways classify the
  //   subscription identically. An earlier, arguably more precise 10305008002000000
  //   («доступ к ПО/базе данных», package 1514296) was switched to this on
  //   2026-08-27 for cross-gateway consistency. ownerType 2 = юрлицо (OOO FASTAGO);
  //   VAT 12% (VAT-registered). Both spic+packageCode verified by a live ofd.soliq.uz cheque.
  // NOTE: the standalone renewal scheduler reads raw process.env (no zod defaults),
  // so these MUST be set explicitly in prod .env for renewal fiscalization to fire.
  OCTO_FISCAL_ENABLED: envBool(true),
  OCTO_FISCAL_SPIC: z.string().default('10305001001000000'),
  OCTO_FISCAL_PACKAGE_CODE: z.string().default('1504169'),
  // ownerType 2 = юрлицо / 1 = ИП.
  OCTO_FISCAL_OWNER_TYPE: z.coerce.number().int().default(2),
  // Line-item VAT rate in percent for the fiscalization API: 0 or 12.
  // 0, not 12: the Payme cassa (configured with the accountant) fiscalizes this
  // service at 0% VAT, and OCTO must match — same комитент (FASTAGO), same
  // product. Prod sets this explicitly; the default only guards a missing var.
  OCTO_FISCAL_VAT_PERCENT: z.coerce.number().int().default(0),
  // Seller id — `commissionInfo.tin` (юрлицо) / `.pinfl` (ИП). Documented as
  // commission-only but the live endpoint REQUIRES it. 306377222 = ООО «FASTAGO»'s
  // real ИНН (the operator's requisites, `apps/web/src/lib/company.ts`) — verified
  // by a live fiscalization that returned an ofd.soliq.uz cheque, 2026-08-27.
  OCTO_FISCAL_INN: z.string().default('306377222'),
  // Cheque-delivery phone — the live endpoint REQUIRES a non-blank
  // `extraInfo.phoneNumber`. We hold no buyer phone at charge time (post-payment
  // capture runs later), so the company line fiscalizes every sale. 998901877798 =
  // FASTAGO's published support number (same requisites source).
  OCTO_FISCAL_FALLBACK_PHONE: z.string().default('998901877798'),
  // Legacy prepare_payment-basket VAT flag (0/1) — unused by the fiscalization API.
  OCTO_FISCAL_NDS: z.coerce.number().int().default(0),

  // Contact open top-up
  TOPUP_OPENS: z.coerce.number().int().default(20),
  TOPUP_PRICE_USD: z.coerce.number().default(20),

  // Lifetime buyer-contact reveals granted to Starter (free) users before the
  // Pro wall. A one-time tally (count of committed TenderOpen rows), NOT a
  // recurring counter — no monthly reset.
  FREE_LIFETIME_CONTACT_OPENS: z.coerce.number().int().default(3),

  // ── Per-account read limits (abuse ceiling on what Pro actually sells) ──
  //
  // Contacts, documents and the CSV export are the only things a Pro
  // subscription buys, and until now nothing limited or watched how fast one
  // account consumed them: no rate limit on those endpoints, no anomaly alert,
  // and nginx logs only Cloudflare's addresses. See `ReadLimitsService` for the
  // measured baseline these numbers come from (90 Pro accounts, 7 days to
  // 2026-09-23: median 13.5 contact views, p95 149, max 216).
  //
  // Defaults live in READ_LIMIT_DEFAULTS above so the numbers are named once and
  // the service, the docs and the schema cannot drift apart. Overridable per
  // deployment because "strange for a human" is a product judgement, not a law.
  READ_LIMIT_CONTACTS_PER_MINUTE: z.coerce
    .number()
    .int()
    .default(READ_LIMIT_DEFAULTS.contactsPerMinute),
  READ_LIMIT_CONTACTS_PER_DAY: z.coerce.number().int().default(READ_LIMIT_DEFAULTS.contactsPerDay),
  READ_LIMIT_DOCUMENTS_PER_MINUTE: z.coerce
    .number()
    .int()
    .default(READ_LIMIT_DEFAULTS.documentsPerMinute),
  READ_LIMIT_DOCUMENTS_PER_DAY: z.coerce
    .number()
    .int()
    .default(READ_LIMIT_DEFAULTS.documentsPerDay),
  // No per-minute twin: an export is one click that streams up to 1000 rows, so
  // the day is the only window that bounds it meaningfully.
  READ_LIMIT_EXPORTS_PER_DAY: z.coerce.number().int().default(READ_LIMIT_DEFAULTS.exportsPerDay),

  // Telegram notifications
  TELEGRAM_ENABLED: envBool(false),
  // Per-cycle digest notifications (replaces the per-tender alert fan-out).
  // Off → the legacy one-message-per-tender path runs unchanged.
  //
  // This field is where the `z.coerce.boolean()` trap was first diagnosed, and it
  // carried its own inline parser for a while. That left TWO contradictory
  // contracts once the other five flags moved to `envBool`:
  // `DIGEST_NOTIFICATIONS=TRUE` read as OFF while every neighbouring flag read
  // the same value as ON, and a typo here was silently OFF while the same typo
  // elsewhere refused to boot. Since this is a LIVE prod switch, that is exactly
  // the silent-wrong-value class the helper exists to end. Now on `envBool` like
  // everything else. Both consumers (notifications.service.ts's
  // ConfigService.get and scheduler.ts's flush-enqueue check) MUST read this
  // exact parsed value so they can never disagree about whether it is on.
  DIGEST_NOTIFICATIONS: envBool(false),
  TELEGRAM_BOT_TOKEN: z.string().optional(),
  // The bot's public @handle, used in the /start + /help inline-mode instructions.
  // Defaults to the prod bot so the copy is correct without an env edit.
  TELEGRAM_BOT_USERNAME: z.string().default('uztender_bot'),
  TELEGRAM_PROMO_CHANNEL: z.string().optional(),
  // Public channel auto-poster (daily big-lot / closing-soon / weekly-winners posts).
  // CHANNEL_ID is the target channel's @handle or numeric -100… id; the poster stays
  // dormant unless CHANNEL_POSTING_ENABLED is explicitly on (envBool, not coerce —
  // see the helper's doc comment for why coerce is banned for a real off-switch).
  CHANNEL_ID: z.string().optional(),
  CHANNEL_POSTING_ENABLED: envBool(false),
  CHANNEL_MIN_LOT_UZS: z.coerce.number().default(500_000_000),
  // Shared secret sent by Telegram in the X-Telegram-Bot-Api-Secret-Token header
  // (set at setWebhook time). Absent → inbound bot disabled (send-only).
  TELEGRAM_WEBHOOK_SECRET: z.string().optional(),
  // Inbound voice-message guards: a voice note longer than
  // TELEGRAM_VOICE_MAX_DURATION_S seconds or larger than TELEGRAM_VOICE_MAX_SIZE_MB
  // megabytes is rejected with a localized error before any download/transcription
  // spend. 20s is enough to ask a search question; 20MB matches Telegram's own
  // getFile download ceiling (Groq's free tier accepts up to 25MB).
  TELEGRAM_VOICE_MAX_DURATION_S: z.coerce.number().int().default(20),
  TELEGRAM_VOICE_MAX_SIZE_MB: z.coerce.number().default(20),
  PROMO_BUDGET_THRESHOLD_UZS: z.coerce.number().default(1_000_000_000),

  // CRM lifecycle bridge — the bot forwards registration/activation/payment
  // events into a Telegram forum topic the team's AI CRM watches. Both OPTIONAL
  // and pre-defaulted to the derived prod target (supergroup + topic), so it
  // works without an env edit. Set CRM_TELEGRAM_CHAT_ID='' to disable (no-op).
  // Chat id derives from the topic link https://t.me/c/3841462698/1440:
  // supergroups are Bot-API-prefixed with -100 → -1003841462698; the topic's
  // thread/root-message id (1440) is the message_thread_id.
  CRM_TELEGRAM_CHAT_ID: z.string().default('-1003841462698'),
  CRM_TELEGRAM_TOPIC_ID: z.coerce.number().int().default(1440),
  // Forum topic (message_thread_id) for TECHNICAL alerts — the scheduler's ops alerts
  // (volume / silence / plausibility / canary) and AlertService pages. Unset = they post
  // into the chat's General, as before. kaztender.com owner, 20.10.2026: too much
  // technical noise in General. The scheduler reads it raw (`opsAlertThreadId`).
  CRM_TELEGRAM_OPS_TOPIC_ID: z.coerce.number().int().optional(),
  // Which CRM posts to forward, comma-separated (`payment,payment_failed,bot_weak,…`;
  // the kinds are `CrmEventKind` in crm-notifier.service.ts). Empty = everything.
  // Kazakhstan (owner, 2026-10-19): no registrations/activations, bot answers only
  // when weak.
  CRM_TELEGRAM_EVENTS: z.string().default(''),

  // --- Error monitoring / alerting (Phase 1) ---
  // Master kill-switch for Telegram error paging. envBool, not coerce, because
  // this is a real off-switch (a typo must not silently read ON).
  ALERTS_ENABLED: envBool(true),
  // Per-fingerprint suppression window: a storming error pages at most once per
  // this many minutes, then rolls up a "×N since last alert" count.
  ALERT_THROTTLE_MINUTES: z.coerce.number().int().default(15),
  // HTTP 500s per 5-minute window before a synthetic critical "error rate" page.
  ERROR_RATE_ALERT_THRESHOLD: z.coerce.number().int().default(20),

  // Email notifications (Postmark) — the fallback channel for users without a
  // deliverable Telegram chat. When POSTMARK_SERVER_TOKEN is unset the stub
  // client is wired (no real sends). EMAIL_FROM must be a Postmark-verified
  // sender signature.
  POSTMARK_SERVER_TOKEN: z.string().optional(),
  EMAIL_FROM: z.string().default('"UzTender" <noreply@uztender.com>'),
  // Where user replies to our mail land.
  //
  // ⚠️ This said "uztender.com is send-only (no MX), so Reply-To must point at a
  // mailbox that actually receives" — the written rationale for routing every
  // reply to a personal address on another domain. The domain has had a Google
  // Workspace MX since 2026-08-12 and `support@uztender.com` is a live group, so
  // the premise is gone. Leaving the comment in place would have had the next
  // reader faithfully reproduce the same off-domain routing.
  SUPPORT_REPLY_TO: z.string().default('support@uztender.com'),
  // Signs unsubscribe links. Falls back to JWT_SECRET so a deploy is never
  // blocked on adding it; set a distinct value in prod.
  UNSUBSCRIBE_SECRET: z.string().optional(),
  // Shared secret for the Postmark deliverability webhook path
  // (POST /notifications/postmark/:secret). Postmark cannot send custom auth
  // headers, so this rides in the URL instead. Unset disables the route (404).
  // The INBOUND webhook (POST /notifications/postmark-inbound/:secret, support
  // letters) uses the same secret — one value to rotate, not two.
  POSTMARK_WEBHOOK_SECRET: z.string().optional(),

  // Inbound support email (2026-10-16): letters to support@ / igor@ reach the API
  // through the Postmark inbound stream and are stored in `support_inbound_emails`
  // either way. These decide whether Нигора ANSWERS them — see
  // docs/explanation/email-infrastructure.md, «Входящая почта поддержки».
  //
  // The switch. ON by default (owner, 2026-10-16: «сразу её включай»). `false`
  // keeps storing letters and showing them to the owner, marked `skipped`
  // (`autoreply_off`), with no answer sent.
  SUPPORT_EMAIL_AUTOREPLY: envBool(true),
  // Who the answers come from. Must sit on a Postmark-verified domain
  // (uztender.com is). A KZ deployment defaults to its own support address.
  SUPPORT_FROM_EMAIL: z.string().default('support@uztender.com'),
  // Copied on EVERY answer, acknowledgements included — the owner sees the whole
  // correspondence (owner, 2026-10-16). There is no «no Cc» on Uzbekistan: an
  // empty value falls back to igor@uztender.com in code. On another country an
  // unset value is empty (`withCountryBrandDefaults`) and means no Cc.
  SUPPORT_OWNER_CC: z.string().default('igor@uztender.com'),
  // Staff senders, comma-separated, treated like the owner: never answered. The
  // owner's own two addresses (igor@crm.deal, igor@uztender.com) are built in.
  SUPPORT_OWNER_EMAILS: z.string().optional(),
  // Automatic answers per Tashkent day, acknowledgements included. Past it a
  // letter goes to the owner unanswered — a mail loop or a flood stops here.
  SUPPORT_EMAIL_DAILY_CAP: z.coerce.number().int().positive().default(60),
  // Answers to ONE sender address in a rolling 24 h, acknowledgements included
  // (owner, 2026-10-16: one person must not be able to spam the assistant). Past
  // it the letter is stored, not answered, and goes to the owner.
  SUPPORT_EMAIL_MAX_PER_SENDER_DAY: z.coerce.number().int().positive().default(5),
  // Minutes between a letter arriving and Нигора answering it (owner, 2026-10-16:
  // she must not answer where he decides to answer himself). The CRM card shows
  // the letter at once with «Отвечу сам»; the answer runs when this has passed,
  // re-reading the conversation then. 0 = answer at once.
  SUPPORT_EMAIL_REPLY_DELAY_MIN: z.coerce.number().int().min(0).default(15),
  // Telegram user id allowed to press «Отвечу сам» / «Пусть отвечает Нигора»
  // outside the CRM group (any member of CRM_TELEGRAM_CHAT_ID may press them
  // there). The owner's own id.
  SUPPORT_OWNER_TELEGRAM_ID: z.string().default('34602240'),
  // The model for support answers — Haiku 5.5 unless set ($0.10/$0.50 per MTok).
  SUPPORT_EMAIL_MODEL: z.string().default('claude-haiku-5-5'),
  // Public base URL of the API (no trailing slash) — used to build absolute
  // links (e.g. the unsubscribe URL) embedded in outbound email. Trailing
  // slash(es) are stripped so an operator-set `.../api/` can't produce a
  // double slash in the composed link.
  API_PUBLIC_URL: z
    .string()
    .url()
    .default('https://api.uztender.com/api')
    .transform((s) => s.replace(/\/+$/, '')),

  // Bright Data UZ-residential proxy — for sources firewalled to UZ IPs
  // (new.cooperation.uz). All optional: when unset, the cooperation source is
  // simply not registered.
  BRIGHT_DATA_RES_PROXY_HOST: z.string().optional(),
  BRIGHT_DATA_RES_PROXY_PORT: z.string().optional(),
  BRIGHT_DATA_RES_PROXY_USER: z.string().optional(),
  BRIGHT_DATA_RES_PROXY_PASS: z.string().optional(),

  // Admin ops page — HTTP Basic-auth credentials for GET /api/admin/overview
  // (internal founder-only health dashboard). When either is unset the route
  // returns 503 (feature disabled) so it can never be reached without creds.
  ADMIN_USER: z.string().optional(),
  ADMIN_PASS: z.string().optional(),

  // Meta (Facebook) Conversions API — server-side conversion events.
  // All optional: when unset the CAPI service is a no-op. The same pixel id is
  // exposed to the browser via VITE_META_PIXEL_ID for the client-side Pixel.
  META_PIXEL_ID: z.string().optional(),
  META_CAPI_ACCESS_TOKEN: z.string().optional(),
  META_TEST_EVENT_CODE: z.string().optional(),

  // Meta Ads Insights — the DAILY SPEND behind the CAC row on /api/admin/growth.
  //
  // Both optional: with either unset the scheduler's `marketing spend` step logs
  // one line and writes nothing, and the CAC tiles say so instead of dividing by
  // a spend of zero. `META_AD_ACCOUNT_ID` has the form `act_<digits>` (the id as
  // Ads Manager prints it, `act_` prefix included) — the Graph edge is
  // `/<account>/insights`.
  //
  // Both are ALSO readable from `app_settings` (`meta_ad_account_id`,
  // `meta_ads_insights_token`) for a host where this file's `.env` is root-only:
  // env wins, the rows are the fallback. See `docs/how-to/growth-charts.md`.
  //
  // ⚠️ `META_ADS_INSIGHTS_TOKEN` falls back to `META_CAPI_ACCESS_TOKEN` when
  // unset — LAST, below the `app_settings` row, because the CAPI token is set on
  // production and would otherwise shadow it. That fallback OFTEN DOES NOT WORK: a CAPI token is issued for
  // sending events and usually carries no `ads_read`. The Graph API answers such
  // a request with a 200-shaped `{"error":{…}}` object rather than a transport
  // failure, so the job logs one warning per run and writes no row — it never
  // records a zero. Give it a token with `ads_read` to get real numbers.
  //
  // ⚠️ Read-only by intent. Everything this token is used for is a GET; a token
  // with `ads_management` on a spending account can create and edit campaigns.
  META_AD_ACCOUNT_ID: z.string().optional(),
  META_ADS_INSIGHTS_TOKEN: z.string().optional(),

  // GA4 Measurement Protocol — the SERVER-side `purchase` event.
  //
  // GA4 received no revenue of any kind: payment fires from a provider webhook
  // and sent Meta a Purchase and the events table a `paid`, but GA4 nothing. It
  // is the only system in the stack with real channel/campaign reporting, so
  // with no revenue in it no channel decision could be revenue-weighted.
  //
  // Both optional: unset ⇒ the sender is dormant. The measurement id is the
  // same one hardcoded in the web's index.html; the API secret is created in
  // GA4 Admin → Data Streams → Measurement Protocol API secrets and is NOT the
  // measurement id — it is a credential and must not reach the browser.
  GA4_MEASUREMENT_ID: z.string().optional(),
  GA4_API_SECRET: z.string().optional(),

  // Server-side Google Ads purchase conversions (Data Manager `events:ingest`)
  // — a first payment that carries a Google click id is reported to the
  // account's conversion action, so Google Ads can bid on purchases.
  //
  // All six optional and all six REQUIRED together: any one unset ⇒ the sender
  // is dormant and sends nothing (`GoogleAdsConversionsService.enabled`).
  // CLIENT_ID / CLIENT_SECRET / REFRESH_TOKEN are an OAuth client whose refresh
  // token carries the `datamanager` scope — credentials, server-only.
  // CUSTOMER_ID is the advertiser account, LOGIN_CUSTOMER_ID its manager
  // account, CONVERSION_ACTION_ID the action the purchases are booked to — all
  // digits, no dashes.
  GOOGLE_ADS_DM_CLIENT_ID: z.string().optional(),
  GOOGLE_ADS_DM_CLIENT_SECRET: z.string().optional(),
  GOOGLE_ADS_DM_REFRESH_TOKEN: z.string().optional(),
  GOOGLE_ADS_CUSTOMER_ID: z.string().optional(),
  GOOGLE_ADS_LOGIN_CUSTOMER_ID: z.string().optional(),
  GOOGLE_ADS_CONVERSION_ACTION_ID: z.string().optional(),

  // PostHog server-side capture — mirrors money-milestone events (paid,
  // registered) that fire server-side (e.g. the Payme webhook) where the
  // browser SDK can't see them. Optional: unset ⇒ the node client stays
  // dormant and every capture is a no-op. The KEY is the same public project
  // key baked into the web build (VITE_POSTHOG_KEY); HOST defaults to US Cloud.
  POSTHOG_KEY: z.string().optional(),
  POSTHOG_HOST: z.string().url().default('https://us.i.posthog.com'),

  // Shared secret for `POST /catalog/prerender-batch` — the bulk detail+similar
  // feed the CI prerender reads instead of two HTTPS calls per page.
  //
  // OPTIONAL and FAIL-CLOSED: unset ⇒ the route answers 404 for everyone. That
  // is the local/dev default (the prerender falls back to the per-id path) and
  // it keeps a public bulk-scrape endpoint from existing by accident. The
  // payload is the ANONYMOUS public view — byte-identical to `GET /catalog/:id`
  // with no Authorization — so a leak costs crawl budget, not Pro data; but 50
  // lots per round-trip is exactly the shape a scraper wants, hence the gate.
  PRERENDER_FEED_TOKEN: z.string().optional(),

  // Per-user feature access (`feature_grants`) — the ROLLOUT override.
  //
  // Comma-separated feature keys from `FEATURE_KEYS`
  // (services/api/src/modules/features/feature-keys.ts) that are open to
  // EVERYONE, grant or no grant. The wave dial: a closed beta runs on rows, and
  // when it graduates the key goes in here for one deploy instead of a script
  // that writes 4,000 grant rows.
  //
  // ⚠️ Empty/unset (the default, including prod) = NOTHING is open to all, so
  // this can only ever widen access and never silently starts a feature. That
  // direction is deliberate and the opposite of OCTO_ALLOWLIST_EMAILS, where
  // empty means "everyone": an allowlist that fails open is fine for a payment
  // METHOD and wrong for an unreleased feature — an unset var in a new
  // environment would show the whole site a half-built page.
  //
  // Unknown keys are ignored rather than rejected: the var outlives the key it
  // names (the row-reading code is deleted once a feature is public), and a
  // stale entry must not fail the API at boot.
  FEATURES_OPEN_TO_ALL: z.string().optional(),
});

export type Env = z.infer<typeof baseSchema>;

/**
 * Brand-facing defaults that follow the DEPLOYMENT, not the codebase.
 *
 * The schema's own defaults for `EMAIL_FROM`, `SUPPORT_REPLY_TO` and
 * `API_PUBLIC_URL` are the Uzbekistan values. On any other deployment an unset
 * variable therefore sent mail signed «UzTender», with replies going to
 * support@uztender.com and an unsubscribe link on api.uztender.com — silently,
 * because the default validates. So for a non-UZ `COUNTRY` those three are
 * filled from that country's `brand` config BEFORE the schema runs.
 *
 * Only ever fills an `undefined` key — an operator-set value always wins — and
 * returns `raw` untouched for UZ (and for a missing/invalid `COUNTRY`, which the
 * schema then defaults or rejects), so the UZ result is byte-identical.
 */
function withCountryBrandDefaults(raw: Record<string, unknown>): Record<string, unknown> {
  const country = raw['COUNTRY'];
  if (typeof country !== 'string' || country === 'uz') return raw;
  const cfg = (publicCountryConfigs as Record<string, PublicCountryConfig | undefined>)[country];
  if (!cfg) return raw;
  const host = new URL(cfg.brand.siteUrl).host;
  const fill: Record<string, string> = {
    EMAIL_FROM: `"${cfg.brand.name}" <no-reply@${host}>`,
    SUPPORT_REPLY_TO: cfg.brand.supportEmail,
    // Inbound support answers must not come from support@uztender.com on KZ.
    SUPPORT_FROM_EMAIL: cfg.brand.supportEmail,
    API_PUBLIC_URL: `https://api.${host}/api`,
    // The schema default names an Uzbek bot; /help on KZ must name its own
    // (@kaztender_com_bot) without anyone remembering an env line.
    ...(cfg.messaging.telegramBot ? { TELEGRAM_BOT_USERNAME: cfg.messaging.telegramBot } : {}),
    // The schema default copies every support answer to igor@uztender.com — on
    // KZ the client would see an Uzbek domain in Cc (audit У8, 16.10). Empty
    // here means «no Cc» on a non-UZ deployment (`SupportEmailAnswerService`);
    // an owner who wants a copy sets the variable.
    SUPPORT_OWNER_CC: '',
  };
  const out: Record<string, unknown> = { ...raw };
  for (const [key, value] of Object.entries(fill)) {
    if (out[key] === undefined) out[key] = value;
  }
  return out;
}

export function parseEnv(raw: Record<string, unknown>): Env {
  const result = baseSchema.safeParse(withCountryBrandDefaults(raw));
  if (!result.success) {
    const messages = result.error.errors
      .map((e) => `  ${e.path.join('.')}: ${e.message}`)
      .join('\n');
    throw new Error(`Environment validation failed:\n${messages}`);
  }
  return result.data;
}
